import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import {
  st,
  Modal,
  MainView,
  Form,
  FormControl,
  FormMessage,
  FormSection,
  Button,
  Message,
  PaymentProfileDecorator,
} from "lib/index";
import { stateSelector, close, fetchProfile, updateProfile, validate, submit, removeProfile } from "./index";
import { stateSelector as paymentSettingsStateSelector } from "../PaymentsSettingsView/index";

class EditPaymentProfileForm extends React.PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      editedLocale: this.props.profile.default_locale,
    };
  }

  handleSubmit(event) {
    event.preventDefault();
    this.props.onSubmit();
  }

  handlePropChange(prop, value) {
    const nextProfile = new PaymentProfileDecorator({
      ...this.props.profile,
      [prop]: value,
    });
    this.props.onChange(nextProfile);
  }

  handleTranslationPropChange(prop, value) {
    const { editedLocale } = this.state;
    const nextProfile = new PaymentProfileDecorator({
      ...this.props.profile,
      translations: this.props.profile.translations
        .filter((t) => t.locale !== editedLocale)
        .concat([{ ...this.props.profile.getTranslation(editedLocale), [prop]: value }]),
    });
    this.props.onChange(nextProfile);
  }

  render() {
    const { editedLocale } = this.state;
    const { profile, payments, valid, errors, loading, onValidate } = this.props;
    return (
      <Form onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={loading}>
        <FormMessage error={errors["base"]} />

        <FormControl.LocaleSelectFormGroup
          value={editedLocale}
          onChange={(editedLocale) => this.setState({ editedLocale })}
          locales="customer_locales"
          label={I18n.t("papp.common.translations_locale")}
          inlineLabel
        />

        <FormControl.InputFormGroup
          {...FormControl.addProps(`payment_profile.name`, { errors })}
          value={profile.getTranslation(editedLocale).name}
          onChange={this.handleTranslationPropChange.bind(this, "name")}
          label={I18n.t("papp.settings.EditPaymentProfileModal.name_label")}
          help={I18n.t("papp.settings.EditPaymentProfileModal.name_hint")}
          required
          autoFocus
        />

        {st.authorize("update", { ...profile, marketplace: true }) && (
          <FormControl.CheckboxFormGroup
            {...FormControl.addProps(`payment_profile.marketplace`, { errors })}
            value={profile.marketplace}
            onChange={this.handlePropChange.bind(this, "marketplace")}
            label={I18n.humanAttributeName("payment_profile", "marketplace")}
            toggle
          />
        )}

        <FormControl.FormGroup inline error={errors[`payment_profile.additional_fee`]}>
          <FormControl.Checkbox
            value={profile.has_additional_fee}
            onChange={this.handlePropChange.bind(this, "has_additional_fee")}
            onBlur={onValidate}
            toggle
            label={I18n.t("papp.settings.EditPaymentProfileModal.has_additional_fee_label")}
          />

          {profile.has_additional_fee && (
            <FormControl.Money
              value={profile.additional_fee_or_percentage}
              onChange={this.handlePropChange.bind(this, "additional_fee_or_percentage")}
              onBlur={onValidate}
              withPercentageCurrency
              defaultCurrency={"percentage"}
              min={0}
            />
          )}

          {profile.has_additional_fee && (
            <FormControl.Checkbox
              value={profile.additional_fee_as_agent_fee}
              onChange={this.handlePropChange.bind(this, "additional_fee_as_agent_fee")}
              onBlur={onValidate}
              toggle
              label={I18n.t("papp.settings.EditPaymentProfileModal.additional_fee_as_agent_fee_label")}
              className="u-marginLeft"
            />
          )}
        </FormControl.FormGroup>

        <FormControl.TextAreaFormGroup
          value={profile.getTranslation(editedLocale).cancellation_policy}
          onChange={this.handleTranslationPropChange.bind(this, "cancellation_policy")}
          label={I18n.humanAttributeName("product_profile", "cancellation_policy")}
          autoHeight
          help={I18n.t("papp.settings.EditPaymentProfileModal.cancellation_policy_hint")}
        />

        {st.authorize("update_st_note", profile) && (
          <FormControl.TextAreaFormGroup
            value={profile.st_note}
            onChange={this.handlePropChange.bind(this, "st_note")}
            label={I18n.humanAttributeName("product_profile", "st_note")}
            autoHeight
          />
        )}

        <h3>{I18n.t("papp.settings.EditPaymentProfileModal.installments_label")}</h3>
        <FormSection.InstallmentsList
          parent={profile}
          payments={payments}
          installments={profile.installments}
          name={`payment_profile.installments`}
          onChange={this.handlePropChange.bind(this, "installments")}
        />

        {!!profile.id && (
          <Message warning visible>
            {I18n.t("papp.settings.EditPaymentProfileModal.profile_change_affects_all_products_warning")}
          </Message>
        )}

        <button type="submit" disabled={loading} hidden />
      </Form>
    );
  }
}

@connect(
  (state) => ({
    opened: stateSelector(state).opened,
    profileId: stateSelector(state).profileId,
    fetchDone: stateSelector(state).profile.fetchDone,
    fetchLoading: stateSelector(state).profile.fetchLoading,
    fetchFailed: stateSelector(state).profile.fetchFailed,
    profile: stateSelector(state).form.model,
    payments: paymentSettingsStateSelector(state).form.initialModel,
    valid: stateSelector(state).form.valid,
    errors: stateSelector(state).form.errors,
    submitLoading: stateSelector(state).form.submitLoading || stateSelector(state).profileRemoval.fetchLoading,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        close,
        fetchProfile,
        updateProfile,
        validate,
        submit,
        removeProfile,
      },
      dispatch
    )
)
export default class EditPaymentProfileModal extends React.PureComponent {
  handleRemoveClick() {
    const { removeProfile } = this.props;
    st.confirm({
      content: I18n.t("papp.settings.EditPaymentProfileModal.remove_confirm_message"),
    }).then((yes) => yes && removeProfile());
  }

  render() {
    const { opened, profileId, profile, fetchLoading, fetchFailed, fetchDone, fetchProfile, close, payments } =
      this.props;
    return (
      opened && (
        <Modal size="large" closeIcon closeOnDimmerClick={false} open onClose={() => close()}>
          <Modal.Header>
            {profileId
              ? I18n.t("papp.settings.EditPaymentProfileModal.edit_profile_title")
              : I18n.t("papp.settings.EditPaymentProfileModal.new_profile_title")}
          </Modal.Header>
          <Modal.Content>
            <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={fetchProfile} />
            {fetchDone && profile && (
              <EditPaymentProfileForm
                profile={profile}
                payments={payments}
                errors={this.props.errors}
                valid={this.props.valid}
                loading={this.props.submitLoading}
                onChange={this.props.updateProfile}
                onValidate={this.props.validate}
                onSubmit={this.props.submit}
              />
            )}
          </Modal.Content>
          {fetchDone && profile && (
            <Modal.Actions>
              <Button as="a" floated="left" basic onClick={close}>
                {I18n.t("papp.common.go_back")}
              </Button>
              {!!profile.id && (
                <Button as="a" floated="left" basic negative onClick={this.handleRemoveClick.bind(this)}>
                  {I18n.t("papp.common.remove")}
                </Button>
              )}
              <Button as="a" primary onClick={this.props.submit} disabled={this.props.submitLoading}>
                {I18n.t("papp.common.save")}
              </Button>
            </Modal.Actions>
          )}
        </Modal>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/settings/EditPaymentProfileModal/EditPaymentProfileModal.jsx